/**
 * Knowing whether the app can be installed, and installing it.
 *
 * A provider rather than a hook because `beforeinstallprompt` fires once, early,
 * and only the listener that was already attached gets it — a component mounting
 * later (like the Profile page) would miss it every time.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isIos, isIosNonSafari, isStandalone } from './push.ts';

/** Not in lib.dom yet. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallPlatform =
  /** Already on the home screen. */
  | 'installed'
  /** Chrome/Edge/Android: the browser will do it for us. */
  | 'prompt'
  /** iOS Safari: only the human can, via the Share sheet. */
  | 'ios-safari'
  /** iOS in a browser that cannot install at all. */
  | 'ios-other'
  /** A desktop browser that never offered. Nothing to show. */
  | 'unavailable';

interface InstallState {
  platform: InstallPlatform;
  installed: boolean;
  /** True when the native prompt is available right now. */
  canPrompt: boolean;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

const InstallContext = createContext<InstallState>({
  platform: 'unavailable',
  installed: false,
  canPrompt: false,
  promptInstall: async () => 'unavailable',
});

export function InstallProvider({ children }: { children: ReactNode }) {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Keep the browser's own banner from firing so the app can offer install
      // where it makes sense (the Profile page) instead.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const value = useMemo<InstallState>(() => {
    const platform: InstallPlatform = installed
      ? 'installed'
      : deferred
        ? 'prompt'
        : isIosNonSafari()
          ? 'ios-other'
          : isIos()
            ? 'ios-safari'
            : 'unavailable';

    return {
      platform,
      installed,
      canPrompt: deferred !== null,
      promptInstall: async () => {
        if (!deferred) return 'unavailable';
        await deferred.prompt();
        const { outcome } = await deferred.userChoice;
        // The event is single-use, whatever they chose.
        setDeferred(null);
        return outcome;
      },
    };
  }, [deferred, installed]);

  return <InstallContext.Provider value={value}>{children}</InstallContext.Provider>;
}

export function useInstall(): InstallState {
  return useContext(InstallContext);
}
